import React, { useState, useEffect } from 'react';
import { 
  Search,
  Filter,
  MapPin,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  UserCircle,
  ChevronRight,
  X,
  Calendar,
  Building2,
  Euro,
  Zap,
  Eye
} from 'lucide-react';
import { fetchInterventionRequests, type InterventionRequest } from '../lib/supabase';

interface BookingListProps {
  onBookingSelect: (id: string) => void;
  company?: any;
}

const statusColors = {
  done: 'bg-green-100 text-green-800',
  ongoing: 'bg-blue-100 text-blue-800',
  action_needed: 'bg-red-100 text-red-800',
  invoiced: 'bg-purple-100 text-purple-800',
  rating_pro: 'bg-yellow-100 text-yellow-800',
  scheduled: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusIcons = {
  done: CheckCircle,
  ongoing: Clock,
  action_needed: AlertCircle,
  invoiced: FileText,
  rating_pro: Star,
  scheduled: Calendar,
  in_progress: Clock,
  completed: CheckCircle,
  cancelled: X
};

const companies = ['Belfius', 'DVV', 'Europ Assistance', 'Verisure'];
const dateRanges = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];

export function BookingList({ onBookingSelect, company }: BookingListProps) {
  const [interventionRequests, setInterventionRequests] = useState<InterventionRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: [] as string[],
    location: [] as string[],
    company: [] as string[],
    dateRange: '',
    proRating: null as number | null
  });

  useEffect(() => {
    loadInterventionRequests();
  }, [company]);

  const loadInterventionRequests = async () => {
    try {
      setIsLoading(true);
      // If company is provided, fetch only that company's requests
      const data = company ? 
        await fetchInterventionRequests(company.id) : 
        await fetchInterventionRequests();
      setInterventionRequests(data);
    } catch (err) {
      setError('Failed to load intervention requests');
      console.error('Error loading intervention requests:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRequests = interventionRequests.filter(request => {
    const locationName = request.location?.name || request.manual_address || '';
    const matchesSearch = 
      request.contact_person.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.service_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      locationName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filters.status.length === 0 || filters.status.includes(request.status);
    const matchesLocation = filters.location.length === 0 || filters.location.includes(locationName);

    return matchesSearch && matchesStatus && matchesLocation;
  });

  const handleFilterChange = (type: keyof typeof filters, value: any) => {
    setFilters(prev => {
      if (Array.isArray(prev[type])) {
        const array = prev[type] as any[];
        const newArray = array.includes(value)
          ? array.filter(item => item !== value)
          : [...array, value];
        return { ...prev, [type]: newArray };
      }
      return { ...prev, [type]: value };
    });
  };

  // Get unique locations for filter
  const uniqueLocations = Array.from(new Set(
    interventionRequests.map(req => req.location?.name || req.manual_address).filter(Boolean)
  ));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <p className="text-gray-600 mt-1">
            {company ? 
              `Manage and track service bookings for ${company.name}` : 
              'Manage and track all service bookings'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFiltersOpen(true)}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters Modal */}
      {isFiltersOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Filter Bookings</h2>
              <button
                onClick={() => setIsFiltersOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Status Filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Status</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(statusColors).map((status) => {
                    const StatusIcon = statusIcons[status as keyof typeof statusIcons];
                    return (
                      <button
                        key={status}
                        onClick={() => handleFilterChange('status', status)}
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          filters.status.includes(status)
                            ? statusColors[status as keyof typeof statusColors]
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <StatusIcon className="h-4 w-4 mr-1.5" />
                        {status.replace('_', ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Location Filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Location</h3>
                <div className="flex flex-wrap gap-2">
                  {uniqueLocations.map((location) => (
                    <button
                      key={location}
                      onClick={() => handleFilterChange('location', location)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filters.location.includes(location)
                          ? 'bg-jaimy-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <MapPin className="h-4 w-4 mr-1.5" />
                      {location}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Date Range</h3>
                <div className="flex flex-wrap gap-2">
                  {dateRanges.map((range) => (
                    <button
                      key={range}
                      onClick={() => handleFilterChange('dateRange', range)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filters.dateRange === range
                          ? 'bg-jaimy-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Calendar className="h-4 w-4 mr-1.5" />
                      {range}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setFilters({
                    status: [],
                    location: [],
                    company: [],
                    dateRange: '',
                    proRating: null
                  });
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Reset Filters
              </button>
              <button
                onClick={() => setIsFiltersOpen(false)}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Booking ID</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Contact</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Service</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Location</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Cost</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Created</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                    No bookings found
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request) => {
                  const StatusIcon = statusIcons[request.status as keyof typeof statusIcons];
                  const locationName = request.location?.name || request.manual_address || 'Manual Address';
                  
                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <span>{request.id.slice(0, 8)}...</span>
                          {request.is_urgent && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <Zap className="h-3 w-3 mr-1" />
                              URGENT
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <UserCircle className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{request.contact_person}</div>
                            <div className="text-sm text-gray-500">{request.contact_phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{request.service_title}</div>
                        <div className="text-sm text-gray-500 capitalize">
                          {request.service_type.replace('_', ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span className="truncate max-w-xs">{locationName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="flex items-center text-gray-900 font-medium">
                            <Euro className="h-4 w-4 mr-1" />
                            {request.total_cost?.toFixed(2) || '0.00'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {request.invoiced ? 'Invoiced' : 'Pending'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          statusColors[request.status as keyof typeof statusColors]
                        }`}>
                          <StatusIcon className="h-4 w-4 mr-1" />
                          {request.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => onBookingSelect(request.id)}
                          className="text-gray-400 hover:text-jaimy-red transition-colors flex items-center gap-1"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}